// Utilities to generate ICS content and Google Calendar links for events
export type IcsEventPayload = {
    title: string;
    description?: string | null;
    location?: string | null;
    startISO: string; // ISO string (UTC or local ISO acceptable)
    endISO?: string | null; // optional; if omitted and not allDay, will default to +1 hour
    allDay?: boolean;
};

function pad(n: number): string { return n.toString().padStart(2, '0'); }

function toCalDateTime(iso: string, allDay?: boolean): { date: string; dt: string } {
    const d = new Date(iso);
    if (allDay) {
        // All-day format YYYYMMDD
        return { date: `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}`, dt: '' };
    }
    // UTC time format YYYYMMDDTHHMMSSZ
    return {
        date: '',
        dt: `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`
    };
}

export function buildICS(payload: IcsEventPayload): string {
    const now = new Date();
    const uid = `${now.getTime()}-${Math.random().toString(36).slice(2)}@griswoldvault`;
    const dtstamp = toCalDateTime(now.toISOString(), false).dt;

    const start = toCalDateTime(payload.startISO, payload.allDay);
    let endISO = payload.endISO || null;
    if (!payload.allDay && !endISO) {
        // default to +1 hour for timed events
        const s = new Date(payload.startISO);
        const e = new Date(s.getTime() + 60 * 60 * 1000);
        endISO = e.toISOString();
    }
    const end = endISO ? toCalDateTime(endISO, payload.allDay) : null;

    const lines: string[] = [];
    lines.push('BEGIN:VCALENDAR');
    lines.push('VERSION:2.0');
    lines.push('PRODID:-//GriswoldVault//EN');
    lines.push('CALSCALE:GREGORIAN');
    lines.push('METHOD:PUBLISH');
    lines.push('BEGIN:VEVENT');
    lines.push(`UID:${uid}`);
    lines.push(`DTSTAMP:${dtstamp}`);
    if (payload.allDay) {
        lines.push(`DTSTART;VALUE=DATE:${start.date}`);
        if (end) {
            // For all-day, DTEND is exclusive; if not provided, treat as single-day (same as DTSTART + 1 day)
            const s = new Date(payload.startISO);
            const endAll = new Date(s.getTime() + 24 * 60 * 60 * 1000);
            lines.push(`DTEND;VALUE=DATE:${toCalDateTime(endAll.toISOString(), true).date}`);
        }
    } else {
        lines.push(`DTSTART:${start.dt}`);
        if (end) lines.push(`DTEND:${end.dt}`);
    }
    if (payload.title) lines.push(`SUMMARY:${escapeIcsText(payload.title)}`);
    if (payload.description) lines.push(`DESCRIPTION:${escapeIcsText(payload.description)}`);
    if (payload.location) lines.push(`LOCATION:${escapeIcsText(payload.location)}`);
    lines.push('END:VEVENT');
    lines.push('END:VCALENDAR');
    return lines.join('\n');
}

export function escapeIcsText(text: string): string {
    return String(text)
        .replace(/\\/g, '\\\\')
        .replace(/\n/g, '\\n')
        .replace(/,/g, '\\,')
        .replace(/;/g, '\\;');
}

export function googleCalendarLink(payload: IcsEventPayload): string {
    const title = encodeURIComponent(payload.title || 'Event');
    const details = encodeURIComponent(payload.description || '');
    const location = encodeURIComponent(payload.location || '');

    let dates = '';
    if (payload.allDay) {
        const s = toCalDateTime(payload.startISO, true).date;
        // For all-day, Google expects end as exclusive next day if multi-day; for single-day supply same day
        const e = s; // single day
        dates = `${s}/${e}`;
    } else {
        const s = toCalDateTime(payload.startISO, false).dt;
        let endISO = payload.endISO;
        if (!endISO) {
            const start = new Date(payload.startISO);
            endISO = new Date(start.getTime() + 60 * 60 * 1000).toISOString();
        }
        const e = toCalDateTime(endISO!, false).dt;
        dates = `${s}/${e}`;
    }

    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&details=${details}&location=${location}&dates=${dates}`;
}
